// Signs an idle admin out.
//
// This console shows every client's phone number, address, bookings and
// payments, and it gets left open on desks. Thirty minutes of nothing, and the
// session goes.
//
// Three things that are easy to get wrong and are handled here:
//
//   * a page refresh must not reset the clock, or leaving a tab open and
//     reloading it would keep someone signed in for ever. The last activity is
//     kept in localStorage, not only in memory.
//   * activity in any tab counts for all of them, through that same key, so
//     working in one tab does not get you signed out of another.
//   * mousemove fires constantly, so it writes at most once every few seconds
//     rather than on every pixel.
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from './supabaseClient';
import { clearCachedPhoto } from './microsoftPhoto';

const STORAGE_KEY = 'livlife.admin.lastActivity';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'focus'];

export default function useIdleLogout({ idleMinutes = 30, warnSeconds = 60 } = {}) {
  const idleMs = idleMinutes * 60 * 1000;
  const warnMs = warnSeconds * 1000;

  const lastWrite = useRef(0);
  const signingOut = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const markActive = useCallback(() => {
    const now = Date.now();
    // At most once every five seconds; mousemove would otherwise write
    // hundreds of times a second for no benefit.
    if (now - lastWrite.current < 5000) return;
    lastWrite.current = now;
    try {
      localStorage.setItem(STORAGE_KEY, String(now));
    } catch (_) {
      // Private browsing, or storage full. The in-memory ref still works for
      // this tab, which is better than crashing the console.
    }
    setSecondsLeft(null);
  }, []);

  const staySignedIn = useCallback(() => {
    lastWrite.current = 0;
    markActive();
  }, [markActive]);

  const signOutNow = useCallback(async () => {
    if (signingOut.current) return;
    signingOut.current = true;
    try {
      const userId = (await supabase.auth.getSession())?.data?.session?.user?.id;
      // Supabase is what authenticates this console, so this is the sign-out
      // that counts. Deliberately no MSAL logoutPopup: a popup from an idle
      // timer is blocked by the browser and would only fail noisily.
      await supabase.auth.signOut();
      clearCachedPhoto(userId);
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* see above */ }
    } catch (err) {
      console.error('Idle sign-out failed:', err);
      signingOut.current = false;
    }
    // RequireAuth is listening to the session and sends them to the login
    // screen on its own, so there is no navigation to do here.
  }, []);

  useEffect(() => {
    markActive();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    const tick = setInterval(() => {
      let last = 0;
      try { last = Number(localStorage.getItem(STORAGE_KEY)) || 0; } catch (_) { /* see above */ }
      if (!last) { markActive(); return; }

      const idleFor = Date.now() - last;
      if (idleFor >= idleMs) {
        signOutNow();
      } else if (idleFor >= idleMs - warnMs) {
        setSecondsLeft(Math.max(0, Math.ceil((idleMs - idleFor) / 1000)));
      } else {
        setSecondsLeft((current) => (current === null ? null : null));
      }
    }, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
      clearInterval(tick);
    };
  }, [idleMs, warnMs, markActive, signOutNow]);

  return { secondsLeft, staySignedIn };
}
